import { Connection, Input, Node, NodeEditor, Output } from "rete"

const defaultLabelOutputKey = (key: string) => key.startsWith("label_")
const defaultCatValInputKey = (key: string) => key.startsWith("catVal_")

/** While true, categorical nodes should not refresh IO from `connectioncreated` (programmatic reconnect). */
const labelOutputReconnectDepthByNodeId = new Map<number, number>()

export function runWithLabelOutputReconnect<T>(node: Node, fn: () => T): T {
  const id = node.id
  labelOutputReconnectDepthByNodeId.set(id, (labelOutputReconnectDepthByNodeId.get(id) ?? 0) + 1)
  try {
    return fn()
  } finally {
    const next = (labelOutputReconnectDepthByNodeId.get(id) ?? 1) - 1
    if (next <= 0) labelOutputReconnectDepthByNodeId.delete(id)
    else labelOutputReconnectDepthByNodeId.set(id, next)
  }
}

export function isLabelOutputReconnectInProgressForNode(nodeId: number): boolean {
  return (labelOutputReconnectDepthByNodeId.get(nodeId) ?? 0) > 0
}

/**
 * Process-wide suppression flag for "seed dynamic IO from the upstream
 * categorical label chain" work — used while `editor.fromJSON` is replaying
 * a saved graph.
 *
 * Why: components like `Categorical to numeric dataset`, `Categorical to
 * boolean datasets`, and `Categorical merge` listen to `connectioncreated`
 * / `connectionremoved` and walk the upstream chain to refresh their
 * dynamic sockets each time. During `fromJSON`, every restored connection
 * fires those events, so for N such components and M connections we burn
 * O(N * M * chain-depth) traversals — re-discovering labels we already
 * have on disk under `node.data.catMapLabels` / `splitLabels` /
 * `mergedLabels`. On a moderately sized model this is the single biggest
 * stall after node render (the existing render-storm fix in `model_view`
 * already addresses React renders).
 *
 * Counter-based so nested `fromJSON` invocations (e.g. undo/redo while
 * initial load is still resolving) don't release the suppression early.
 */
let chainSeedSuppressionDepth = 0

export function suspendCategoricalChainSeeding(): void {
  chainSeedSuppressionDepth += 1
}

export function resumeCategoricalChainSeeding(): void {
  if (chainSeedSuppressionDepth > 0) chainSeedSuppressionDepth -= 1
}

export function isCategoricalChainSeedingSuppressed(): boolean {
  return chainSeedSuppressionDepth > 0
}

export function normalizeWireLabel(label: string): string {
  return label.trim().toLowerCase()
}

function shallowCloneConnectionData(data: unknown): Record<string, unknown> {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return { ...(data as Record<string, unknown>) }
  }
  return {}
}

export type LabelOutputWireSnapshot = {
  normalizedLabel: string
  /** Case-sensitive `String(name).trim()` at snapshot time (extra match path). */
  trimmedLabel: string
  /** Rete output key, e.g. `label_3`. */
  sourceOutputKey: string
  targetNodeId: number
  targetInputKey: string
  data: Record<string, unknown>
}

/** Serialized on `node.data` while the categorical input is unwired; restored on reconnect by label name. */
export const PENDING_LABEL_OUTPUT_WIRE_DATA_KEY = "_pendingLabelOutputWireSnapshots"

function wireTargetKey(s: Pick<LabelOutputWireSnapshot, "targetNodeId" | "targetInputKey">): string {
  return `${s.targetNodeId}:${s.targetInputKey}`
}

function cloneLabelOutputWireSnapshot(s: LabelOutputWireSnapshot): LabelOutputWireSnapshot {
  return {
    normalizedLabel: s.normalizedLabel,
    trimmedLabel: s.trimmedLabel,
    sourceOutputKey: s.sourceOutputKey,
    targetNodeId: s.targetNodeId,
    targetInputKey: s.targetInputKey,
    data: shallowCloneConnectionData(s.data),
  }
}

function parseStoredLabelOutputWireSnapshot(raw: unknown): LabelOutputWireSnapshot | null {
  if (!raw || typeof raw !== "object") return null
  const o = raw as Record<string, unknown>
  const targetNodeId = Number(o.targetNodeId)
  const targetInputKey = String(o.targetInputKey ?? "")
  if (!Number.isFinite(targetNodeId) || !targetInputKey) return null
  const normalizedLabel = typeof o.normalizedLabel === "string" ? o.normalizedLabel : normalizeWireLabel(String(o.normalizedLabel ?? ""))
  const trimmedLabel = typeof o.trimmedLabel === "string" ? o.trimmedLabel : String(o.trimmedLabel ?? "").trim()
  const sourceOutputKey = String(o.sourceOutputKey ?? "")
  const data = o.data && typeof o.data === "object" && !Array.isArray(o.data) ? { ...(o.data as Record<string, unknown>) } : {}
  return {
    normalizedLabel,
    trimmedLabel,
    sourceOutputKey,
    targetNodeId,
    targetInputKey,
    data,
  }
}

/**
 * Merges live graph snapshots with memory from before the categorical wire was dropped.
 * For the same downstream input, the **live** snapshot wins when present (current truth).
 */
export function mergeLabelOutputWireSnapshotsForReconnect(
  live: LabelOutputWireSnapshot[],
  pending: LabelOutputWireSnapshot[]
): LabelOutputWireSnapshot[] {
  const map = new Map<string, LabelOutputWireSnapshot>()
  pending.forEach(s => map.set(wireTargetKey(s), cloneLabelOutputWireSnapshot(s)))
  live.forEach(s => map.set(wireTargetKey(s), cloneLabelOutputWireSnapshot(s)))
  return Array.from(map.values())
}

const MAX_PENDING_LABEL_OUTPUT_SNAPSHOTS = 400

/** Accumulates per-label output → downstream input wiring before sockets are pruned (e.g. input rewired). */
export function rememberLabelOutputWireSnapshots(node: Node, snapshots: LabelOutputWireSnapshot[]): void {
  if (snapshots.length === 0) return
  const prevRaw = node.data[PENDING_LABEL_OUTPUT_WIRE_DATA_KEY]
  const prev: LabelOutputWireSnapshot[] = Array.isArray(prevRaw)
    ? prevRaw.map(parseStoredLabelOutputWireSnapshot).filter((s): s is LabelOutputWireSnapshot => s != null)
    : []
  const map = new Map<string, LabelOutputWireSnapshot>()
  prev.forEach(s => map.set(wireTargetKey(s), cloneLabelOutputWireSnapshot(s)))
  snapshots.forEach(s => map.set(wireTargetKey(s), cloneLabelOutputWireSnapshot(s)))
  node.data[PENDING_LABEL_OUTPUT_WIRE_DATA_KEY] = Array.from(map.values()).slice(-MAX_PENDING_LABEL_OUTPUT_SNAPSHOTS)
}

/** Returns pending snapshots and clears them (typically after a new categorical connection supplies labels). */
export function takePendingLabelOutputWireSnapshots(node: Node): LabelOutputWireSnapshot[] {
  const raw = node.data[PENDING_LABEL_OUTPUT_WIRE_DATA_KEY]
  delete node.data[PENDING_LABEL_OUTPUT_WIRE_DATA_KEY]
  if (!Array.isArray(raw)) return []
  return raw.map(parseStoredLabelOutputWireSnapshot).filter((s): s is LabelOutputWireSnapshot => s != null)
}

/**
 * Records downstream wiring for dynamic per-label outputs before those outputs
 * are removed/recreated (e.g. when category values or merge indices change).
 */
export function snapshotLabelOutputConnections(
  node: Node,
  isLabelOutputKey: (key: string) => boolean = defaultLabelOutputKey
): LabelOutputWireSnapshot[] {
  const results: LabelOutputWireSnapshot[] = []
  for (const output of Array.from(node.outputs.values())) {
    if (!isLabelOutputKey(output.key)) continue
    const rawName = String(output.name ?? "")
    const normalizedLabel = normalizeWireLabel(rawName)
    const trimmedLabel = rawName.trim()
    for (const c of [...output.connections] as Connection[]) {
      const input = c.input
      if (!input.node) continue
      results.push({
        normalizedLabel,
        trimmedLabel,
        sourceOutputKey: output.key,
        targetNodeId: input.node.id,
        targetInputKey: input.key,
        data: shallowCloneConnectionData(c.data),
      })
    }
  }
  return results
}

function outputMatchesLabelSnapshot(o: Output, snap: LabelOutputWireSnapshot, isLabelOutputKey: (key: string) => boolean): boolean {
  if (!isLabelOutputKey(o.key)) return false
  const raw = String(o.name ?? "")
  if (normalizeWireLabel(raw) === snap.normalizedLabel) return true
  if (snap.trimmedLabel.length > 0 && raw.trim() === snap.trimmedLabel) return true
  return false
}

function sortIoByKey<T extends { key: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }))
}

/**
 * Reconnects snapshots to outputs on `node` whose display name matches the same
 * normalized label (trim + lowercase). Skips targets that already have a connection
 * (including unchanged IO). Socket order on the node does not matter; among name matches
 * the same stable key order is used when several outputs share a title.
 */
export function reconnectLabelOutputsByNormalizedLabel(
  editor: NodeEditor,
  node: Node,
  snapshots: LabelOutputWireSnapshot[],
  isLabelOutputKey: (key: string) => boolean = defaultLabelOutputKey
): void {
  for (const snap of snapshots) {
    const targetNode = editor.nodes.find(n => n.id === snap.targetNodeId)
    if (!targetNode) continue
    const targetInput = targetNode.inputs.get(snap.targetInputKey)
    if (!targetInput || targetInput.hasConnection()) continue

    const nameMatches = sortIoByKey(
      Array.from(node.outputs.values()).filter(o => outputMatchesLabelSnapshot(o, snap, isLabelOutputKey))
    )
    const match = nameMatches[0]
    if (!match) continue
    if (match.connectedTo(targetInput)) continue

    try {
      editor.connect(match, targetInput, snap.data)
    } catch {
      // incompatible sockets or Rete guard (e.g. output already has a connection when not multi)
    }
  }
}

export type LabelInputWireSnapshot = {
  normalizedLabel: string
  trimmedLabel: string
  sourceNodeId: number
  sourceOutputKey: string
  data: Record<string, unknown>
}

export const PENDING_LABEL_INPUT_WIRE_DATA_KEY = "_pendingLabelInputWireSnapshots"

function wireSourceKey(s: Pick<LabelInputWireSnapshot, "sourceNodeId" | "sourceOutputKey">): string {
  return `${s.sourceNodeId}:${s.sourceOutputKey}`
}

function cloneLabelInputWireSnapshot(s: LabelInputWireSnapshot): LabelInputWireSnapshot {
  return {
    normalizedLabel: s.normalizedLabel,
    trimmedLabel: s.trimmedLabel,
    sourceNodeId: s.sourceNodeId,
    sourceOutputKey: s.sourceOutputKey,
    data: shallowCloneConnectionData(s.data),
  }
}

function parseStoredLabelInputWireSnapshot(raw: unknown): LabelInputWireSnapshot | null {
  if (!raw || typeof raw !== "object") return null
  const o = raw as Record<string, unknown>
  const sourceNodeId = Number(o.sourceNodeId)
  const sourceOutputKey = String(o.sourceOutputKey ?? "")
  if (!Number.isFinite(sourceNodeId) || !sourceOutputKey) return null
  const rawName = String(o.normalizedLabel ?? o.trimmedLabel ?? "")
  const normalizedLabel = typeof o.normalizedLabel === "string" ? o.normalizedLabel : normalizeWireLabel(rawName)
  const trimmedLabel = typeof o.trimmedLabel === "string" ? o.trimmedLabel : rawName.trim()
  const data = o.data && typeof o.data === "object" && !Array.isArray(o.data) ? { ...(o.data as Record<string, unknown>) } : {}
  return { normalizedLabel, trimmedLabel, sourceNodeId, sourceOutputKey, data }
}

export function mergeLabelInputWireSnapshotsForReconnect(
  live: LabelInputWireSnapshot[],
  pending: LabelInputWireSnapshot[]
): LabelInputWireSnapshot[] {
  const map = new Map<string, LabelInputWireSnapshot>()
  pending.forEach(s => map.set(wireSourceKey(s), cloneLabelInputWireSnapshot(s)))
  live.forEach(s => map.set(wireSourceKey(s), cloneLabelInputWireSnapshot(s)))
  return Array.from(map.values())
}

export function rememberLabelInputWireSnapshots(node: Node, snapshots: LabelInputWireSnapshot[]): void {
  if (snapshots.length === 0) return
  const prevRaw = node.data[PENDING_LABEL_INPUT_WIRE_DATA_KEY]
  const prev: LabelInputWireSnapshot[] = Array.isArray(prevRaw)
    ? prevRaw.map(parseStoredLabelInputWireSnapshot).filter((s): s is LabelInputWireSnapshot => s != null)
    : []
  const map = new Map<string, LabelInputWireSnapshot>()
  prev.forEach(s => map.set(wireSourceKey(s), cloneLabelInputWireSnapshot(s)))
  snapshots.forEach(s => map.set(wireSourceKey(s), cloneLabelInputWireSnapshot(s)))
  node.data[PENDING_LABEL_INPUT_WIRE_DATA_KEY] = Array.from(map.values()).slice(-MAX_PENDING_LABEL_OUTPUT_SNAPSHOTS)
}

export function takePendingLabelInputWireSnapshots(node: Node): LabelInputWireSnapshot[] {
  const raw = node.data[PENDING_LABEL_INPUT_WIRE_DATA_KEY]
  delete node.data[PENDING_LABEL_INPUT_WIRE_DATA_KEY]
  if (!Array.isArray(raw)) return []
  return raw.map(parseStoredLabelInputWireSnapshot).filter((s): s is LabelInputWireSnapshot => s != null)
}

/**
 * Records upstream wiring for dynamic per-label inputs (e.g. `catVal_*`) before inputs are rebuilt.
 */
export function snapshotLabelInputConnections(
  node: Node,
  isDynamicInputKey: (key: string) => boolean = defaultCatValInputKey
): LabelInputWireSnapshot[] {
  const results: LabelInputWireSnapshot[] = []
  for (const input of Array.from(node.inputs.values())) {
    if (!isDynamicInputKey(input.key)) continue
    const rawName = String(input.name ?? "")
    const normalizedLabel = normalizeWireLabel(rawName)
    const trimmedLabel = rawName.trim()
    for (const c of [...input.connections] as Connection[]) {
      const output = c.output
      if (!output.node) continue
      results.push({
        normalizedLabel,
        trimmedLabel,
        sourceNodeId: output.node.id,
        sourceOutputKey: output.key,
        data: shallowCloneConnectionData(c.data),
      })
    }
  }
  return results
}

function inputMatchesLabelSnapshot(inp: Input, snap: LabelInputWireSnapshot, isDynamicInputKey: (key: string) => boolean): boolean {
  if (!isDynamicInputKey(inp.key)) return false
  const raw = String(inp.name ?? "")
  if (normalizeWireLabel(raw) === snap.normalizedLabel) return true
  if (snap.trimmedLabel.length > 0 && raw.trim() === snap.trimmedLabel) return true
  return false
}

/**
 * Reconnects upstream wires to dynamic inputs whose titles match the snapshot.
 * Ignores Rete map insertion order: matches are sorted by input key, then the first
 * socket that is still free receives the connection.
 */
export function reconnectLabelInputsByNormalizedLabel(
  editor: NodeEditor,
  node: Node,
  snapshots: LabelInputWireSnapshot[],
  isDynamicInputKey: (key: string) => boolean = defaultCatValInputKey
): void {
  for (const snap of snapshots) {
    const sourceNode = editor.nodes.find(n => n.id === snap.sourceNodeId)
    if (!sourceNode) continue
    const sourceOutput = sourceNode.outputs.get(snap.sourceOutputKey)
    if (!sourceOutput) continue

    const nameMatches = sortIoByKey(
      Array.from(node.inputs.values()).filter(inp => inputMatchesLabelSnapshot(inp, snap, isDynamicInputKey))
    )
    const newInput = nameMatches.find(inp => !inp.hasConnection())
    if (!newInput) continue

    try {
      editor.connect(sourceOutput, newInput, snap.data)
    } catch {
      // incompatible or guards
    }
  }
}
